import { useCallback, useEffect, useMemo, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "./App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PhotoProvider } from "./context/PhotoContext";
import { api } from "./lib/api";
import { nextMatchday } from "./padel/helpers";
import { Header } from "./padel/Header";
import { Hero } from "./padel/Hero";
import { ScheduleView } from "./padel/ScheduleView";
import { StandingsView } from "./padel/StandingsView";
import { PagellaView } from "./padel/PagellaView";
import { MatchdayPagella } from "./padel/MatchdayPagella";
import { ResultsView } from "./padel/ResultsView";
import { PlayerStatsView } from "./padel/PlayerStatsView";
import { PairsView } from "./padel/PairsView";
import { PlayerDetailDialog } from "./padel/PlayerDetailDialog";
import { RulesView } from "./padel/RulesView";
import { AchievementsView } from "./padel/AchievementsView";
import { ConductView } from "./padel/ConductView";
import { FantaPadelView } from "./padel/FantaPadelView";
import { PublicProfile } from "./padel/PublicProfile";
import { PrintQR } from "./padel/PrintQR";
import { NextMatchCountdown } from "./padel/NextMatchCountdown";
import { RivalryCard } from "./padel/RivalryCard";
import { TVMode } from "./padel/TVMode";
import { ChatWidget } from "./padel/ChatWidget";
import { Toaster } from "./components/ui/sonner";
import { CalendarDays, ListOrdered, ClipboardEdit, BarChart3, BookOpen, Loader2, Users, Medal, Scale, Gamepad2 } from "lucide-react";

const TABS = [
  { id: "schedule", label: "Prossima Giornata", icon: CalendarDays },
  { id: "standings", label: "Classifica", icon: ListOrdered },
  { id: "results", label: "Risultati", icon: ClipboardEdit },
  { id: "stats", label: "Giocatori", icon: BarChart3 },
  { id: "pairs", label: "Coppie", icon: Users },
  { id: "achievements", label: "Albo d'Oro", icon: Medal },
  { id: "fanta", label: "Fantapadel", icon: Gamepad2 },
  { id: "conduct", label: "Fair-Play", icon: Scale },
  { id: "rules", label: "Regolamento", icon: BookOpen },
];

function Dashboard() {
  const { isAdmin, ready } = useAuth();
  const [players, setPlayers] = useState([]);
  const [matchdays, setMatchdays] = useState([]);
  const [standings, setStandings] = useState([]);
  const [tab, setTab] = useState("schedule");
  const [loading, setLoading] = useState(true);
  const [tvOpen, setTvOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const load = useCallback(async () => {
    const [p, m, s] = await Promise.all([
      api.get("/players"),
      api.get("/matchdays"),
      api.get("/standings"),
    ]);
    setPlayers(p.data);
    setMatchdays(m.data);
    setStandings(s.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refetch = useCallback(async () => {
    const [m, s] = await Promise.all([api.get("/matchdays"), api.get("/standings")]);
    setMatchdays(m.data);
    setStandings(s.data);
  }, []);

  const refetchPlayers = useCallback(async () => {
    const p = await api.get("/players");
    setPlayers(p.data);
  }, []);

  const playersMap = useMemo(() => Object.fromEntries(players.map((p) => [p.id, p])), [players]);
  const photoMap = useMemo(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    return Object.fromEntries(
      players
        .filter((p) => p.has_photo)
        .map((p) => [p.id, `${base}/api/players/${p.id}/photo?v=${p.photo_version}`])
    );
  }, [players]);
  const nextMd = useMemo(() => nextMatchday(matchdays), [matchdays]);

  if (loading || !ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0F19]">
        <Loader2 className="w-10 h-10 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <PhotoProvider value={photoMap}>
    <div className="min-h-screen bg-[#0B0F19] ap-grain">
      <Header onTvMode={() => setTvOpen(true)} />
      <Hero matchdays={matchdays} standings={standings} nextMd={nextMd} />

      <nav className="sticky top-[61px] z-40 ap-glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex gap-1 overflow-x-auto no-scrollbar py-2.5">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`relative flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap font-semibold uppercase text-xs tracking-wide transition-colors ${
                  active ? "text-slate-950" : "text-slate-400 hover:text-white hover:bg-slate-800/60"
                }`}
              >
                {active && (
                  <motion.span
                    layoutId="activeTabIndicator"
                    className="absolute inset-0 rounded-full bg-emerald-500 ap-glow-emerald"
                    transition={{ type: "spring", stiffness: 420, damping: 34 }}
                  />
                )}
                <span className="relative z-10 flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  {t.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          >
            {tab === "schedule" && (
              <div className="space-y-8">
                <NextMatchCountdown nextMd={nextMd} onPlay={() => setTab("fanta")} />
                <RivalryCard onSelectPlayer={setSelectedPlayer} />
                <MatchdayPagella playersMap={playersMap} onSelectPlayer={setSelectedPlayer} />
                <ScheduleView matchday={nextMd} playersMap={playersMap} />
              </div>
            )}
            {tab === "standings" && (
              <div className="space-y-10">
                <StandingsView standings={standings} onSelectPlayer={setSelectedPlayer} />
                <PagellaView playersMap={playersMap} onSelectPlayer={setSelectedPlayer} />
              </div>
            )}
            {tab === "results" && (
              <ResultsView
                matchdays={matchdays}
                playersMap={playersMap}
                isAdmin={isAdmin}
                onSaved={refetch}
              />
            )}
            {tab === "stats" && (
              <PlayerStatsView standings={standings} onSelectPlayer={setSelectedPlayer} />
            )}
            {tab === "pairs" && <PairsView playersMap={playersMap} />}
            {tab === "achievements" && <AchievementsView onSelectPlayer={setSelectedPlayer} />}
            {tab === "fanta" && <FantaPadelView />}
            {tab === "conduct" && <ConductView isAdmin={isAdmin} matchdays={matchdays} />}
            {tab === "rules" && <RulesView />}
          </motion.div>
        </AnimatePresence>
      </main>

      <footer className="relative z-10 border-t border-white/10 mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
          <span className="font-mono tracking-wider">AMERICAN PADEL · STAGIONE 2026</span>
          <span>{isAdmin ? "Modalità amministratore attiva" : "Visualizzazione pubblica"}</span>
        </div>
      </footer>

      {tvOpen && (
        <TVMode
          matchday={nextMd}
          standings={standings}
          playersMap={playersMap}
          onClose={() => setTvOpen(false)}
        />
      )}

      <PlayerDetailDialog
        playerId={selectedPlayer}
        onClose={() => setSelectedPlayer(null)}
        isAdmin={isAdmin}
        onPhotoChange={refetchPlayers}
      />
      <ChatWidget />
    </div>
    </PhotoProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/p/:playerId" element={<PublicProfile />} />
          <Route path="/print/qr" element={<PrintQR />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="top-center" richColors />
    </AuthProvider>
  );
}

export default App;
